import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { AddParcelItemTypeDto, ParcelItemTypesApi } from '@buspro/web/apis';
import { ErrorDisplayComponent, ExtendedProblemDetail, useFormErrorHandler } from '@buspro/web/core';
import { injectMutation, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NzFormControlComponent, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { NzModalComponent, NzModalContentDirective, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-add-parcel-item-type',
  templateUrl: './add-parcel-item-type.component.html',
  styleUrls: ['./add-parcel-item-type.component.css'],
  imports: [
    NzModalComponent,
    NzModalTitleDirective,
    NzModalContentDirective,
    ErrorDisplayComponent,
    ReactiveFormsModule,
    NzFormItemComponent,
    NzInputDirective,
    NzFormLabelComponent,
    NzFormControlComponent,
  ],
  standalone: true,
})
export class AddParcelItemTypeComponent {
  private formBuilder = inject(FormBuilder);
  private parcelItemTypesApi = inject(ParcelItemTypesApi);
  private notification = inject(NzNotificationService);
  private queryClient = injectQueryClient();

  // Standardized error handling
  private readonly formErrorHandler = useFormErrorHandler();
  showError = signal(false);
  errorDetails: ExtendedProblemDetail | null = null;

  form = this.formBuilder.group({
    category: ['', [Validators.required]],
    description: ['', [Validators.required]],
    maxWeight: [0, [Validators.min(0)]],
    maxVolume: [0, [Validators.min(0)]],
    defaultCost: [10000, [Validators.required, Validators.min(0)]],
  });

  @Input()
  show = false;

  @Output()
  hide = new EventEmitter(true);

  // Tanstack Query mutation for adding parcel item type
  addMutation = injectMutation(() => ({
    mutationFn: (dto: AddParcelItemTypeDto) =>
      lastValueFrom(this.parcelItemTypesApi.parcelItemTypesControllerAdd({ body: dto })),
    onSuccess: () => {
      // Invalidate and refetch parcel item types queries
      this.queryClient.invalidateQueries({ queryKey: ['parcel-item-types'] });

      this.clearErrors();
      this.show = false;
      this.closeDrawer();
      this.form.reset();
      this.notification.success('✅ Success', 'Parcel item type has been added successfully');
    },
    onError: (error: any) => {
      console.error('Error adding parcel item type:', error);
      const result = this.formErrorHandler.handleFormError(error, { formName: 'tenant-add-parcel-item-type-form' });
      this.errorDetails = result.problemDetail ?? null;
      this.showError.set(!!this.errorDetails);
    },
  }));

  closeDrawer() {
    this.hide.emit(true);
    this.show = false;
    this.form.reset();
  }

  private clearErrors() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  onDismissError() {
    this.clearErrors();
  }

  add() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.clearErrors();

    const payload: AddParcelItemTypeDto = {
      category: this.form.get('category')!.value!,
      description: this.form.get('description')!.value!,
      maxWeight: Number(this.form.get('maxWeight')!.value!),
      maxVolume: Number(this.form.get('maxVolume')!.value!),
      defaultCost: Number(this.form.get('defaultCost')!.value!),
    };

    this.addMutation.mutate(payload);
  }
}
